import type { Shipment } from './api'

// How long a manager's rejection stays visible to the driver. After that the trip simply goes on
// without an old notice hanging on the screen.
const REJECTION_VISIBLE_MS = 12 * 60 * 60 * 1000
const MINUTE_MS = 60_000

export type CompletionRequestState =
  | { kind: 'none' }
  // The driver asked to finish; the trip completes on its own at dueAt unless the manager answers.
  | { kind: 'pending'; requestedAt: Date | null; dueAt: Date; remainingMs: number }
  // The manager turned the last request down — the trip goes on.
  | { kind: 'rejected'; rejectedAt: Date }

type CompletionFields = Pick<Shipment, 'status' | 'completion_requested_at' | 'completion_due_at' | 'completion_rejected_at'>

function parse(value?: string | null): Date | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** Where the driver's request to finish the trip stands, as both cabinets show it. */
export function getCompletionRequestState(shipment: CompletionFields, now: Date = new Date()): CompletionRequestState {
  // Only a running trip has a request to answer; a finished or cancelled one says so by its status.
  if (shipment.status !== 'in_progress') return { kind: 'none' }

  const dueAt = parse(shipment.completion_due_at)
  if (dueAt) {
    return {
      kind: 'pending',
      requestedAt: parse(shipment.completion_requested_at),
      dueAt,
      remainingMs: Math.max(0, dueAt.getTime() - now.getTime()),
    }
  }

  const rejectedAt = parse(shipment.completion_rejected_at)
  if (rejectedAt && now.getTime() - rejectedAt.getTime() < REJECTION_VISIBLE_MS) {
    return { kind: 'rejected', rejectedAt }
  }
  return { kind: 'none' }
}

/**
 * Time left until the trip completes on its own: «1 ч 05 мин», «12 мин», «меньше минуты».
 * Rounded up to the minute, so the label never claims less time than is left.
 */
export function formatCompletionCountdown(remainingMs: number): string {
  const safe = Number.isFinite(remainingMs) ? Math.max(0, remainingMs) : 0
  if (safe < MINUTE_MS) return safe === 0 ? 'сейчас' : 'меньше минуты'
  const totalMinutes = Math.ceil(safe / MINUTE_MS)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours === 0) return `${minutes} мин`
  return `${hours} ч ${String(minutes).padStart(2, '0')} мин`
}

/** The sentence both cabinets use for the countdown. */
export function describeAutoCompletion(remainingMs: number): string {
  const left = formatCompletionCountdown(remainingMs)
  return left === 'сейчас' ? 'Заявка завершится автоматически в ближайшую минуту' : `Заявка завершится автоматически через ${left}`
}

/**
 * Merges a fresh copy of a shipment into a kept one. The backend omits empty fields, so a plain
 * spread would keep a request that has since been withdrawn, rejected or completed.
 */
export function mergeFreshShipment<T extends Shipment>(kept: T, fresh: Shipment): T {
  return {
    ...kept,
    ...fresh,
    completion_requested_at: fresh.completion_requested_at ?? null,
    completion_due_at: fresh.completion_due_at ?? null,
    completion_rejected_at: fresh.completion_rejected_at ?? null,
    control_acknowledgements: fresh.control_acknowledgements ?? null,
  }
}
